import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import {
  StorageAPIResponse,
  FileUploadResponse,
  UploadOptions,
  UploadProgress,
  FileMetadata,
  FileCategory,
  SupportedFileTypes,
  BatchUploadResult,
  StorageErrorCode,
} from '@/types/storage.types';

/**
 * Storage Service
 * Handles file upload operations to cloud storage
 * Following DEVGUIDE.md standards - NO hardcoded URLs
 */
class StorageService {
  // ==========================================
  // FILE UPLOAD OPERATIONS
  // ==========================================

  /**
   * Upload a single file to storage
   * @param file - File to upload
   * @param options - Upload options including progress callback
   * @returns Upload response with file metadata
   */
  async uploadFile(file: File, options?: UploadOptions): Promise<FileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    // Add metadata if provided
    if (options?.metadata) {
      Object.entries(options.metadata).forEach(([key, value]) => {
        formData.append(key, String(value));
      });
    }

    try {
      const response = await apiClient.put<StorageAPIResponse<FileUploadResponse>>(
        API_ENDPOINTS.STORAGE.UPLOAD,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (options?.onProgress && progressEvent.total) {
              const progress: UploadProgress = {
                loaded: progressEvent.loaded,
                total: progressEvent.total,
                percentage: Math.round((progressEvent.loaded * 100) / progressEvent.total),
              };
              options.onProgress(progress);
            }
          },
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to upload file');
      }

      // Return the upload data - API now provides name, key, url, fileType
      const uploadData = response.data.data;
      
      // Ensure we have all required fields
      if (!uploadData.url || !uploadData.key) {
        throw new Error('Invalid storage response: missing URL or key');
      }
      
      return uploadData;
    } catch (error: any) {
      // Handle specific error codes
      if (error.response?.data?.error?.error_code) {
        const errorCode = error.response.data.error.error_code;
        const errorMessage = error.response.data.error.error_description;
        
        switch (errorCode) {
          case StorageErrorCode.FILE_TOO_LARGE:
            throw new Error(`File is too large. ${errorMessage}`);
          case StorageErrorCode.UNSUPPORTED_FILE_TYPE:
            throw new Error(`File type not supported. ${errorMessage}`);
          case StorageErrorCode.UNAUTHORIZED:
            throw new Error('You are not authorized to upload files.');
          default:
            throw new Error(errorMessage || 'Failed to upload file');
        }
      }
      
      throw error;
    }
  }

  /**
   * Upload multiple files in batch
   * @param files - Array of files to upload
   * @param options - Upload options
   * @returns Batch upload results
   */
  async uploadFiles(files: File[], options?: UploadOptions): Promise<BatchUploadResult> {
    const result: BatchUploadResult = {
      successful: [],
      failed: [],
      total: files.length,
      successCount: 0,
      failureCount: 0,
    };

    // Upload files sequentially to avoid overwhelming the server
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        // Calculate overall progress for batch upload
        const batchProgress = options?.onProgress;
        const fileOptions: UploadOptions = {
          ...options,
          onProgress: batchProgress ? (progress) => {
            // Calculate overall batch progress
            const overallProgress: UploadProgress = {
              loaded: (i * 100) + progress.percentage,
              total: files.length * 100,
              percentage: Math.round(((i * 100) + progress.percentage) / files.length),
            };
            batchProgress(overallProgress);
          } : undefined,
        };

        const response = await this.uploadFile(file, fileOptions);
        result.successful.push(response);
        result.successCount++;
      } catch (error: any) {
        result.failed.push({
          file,
          error: error.message || 'Upload failed',
        });
        result.failureCount++;
      }
    }

    return result;
  }

  // ==========================================
  // UTILITY METHODS
  // ==========================================

  /**
   * Validate file before upload
   * @param file - File to validate
   * @returns True if file is valid
   */
  validateFile(file: File): { valid: boolean; error?: string } {
    // Check file size (10MB limit as example)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return {
        valid: false,
        error: `File size exceeds ${maxSize / (1024 * 1024)}MB limit`,
      };
    }

    // Check file type
    const extension = this.getFileExtension(file.name);
    if (!this.isSupportedFileType(extension)) {
      return {
        valid: false,
        error: `File type .${extension} is not supported`,
      };
    }

    return { valid: true };
  }

  /**
   * Get file metadata
   * @param file - File to analyze
   * @returns File metadata
   */
  getFileMetadata(file: File): FileMetadata {
    const extension = this.getFileExtension(file.name);
    const category = this.getFileCategory(extension);

    return {
      name: file.name,
      size: file.size,
      type: file.type || 'application/octet-stream',
      lastModified: file.lastModified,
      category,
      extension,
    };
  }

  /**
   * Get file extension from filename
   * @param filename - Name of the file
   * @returns File extension in lowercase
   */
  private getFileExtension(filename: string): string {
    const parts = filename.split('.');
    return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
  }

  /**
   * Check if file type is supported
   * @param extension - File extension
   * @returns True if supported
   */
  private isSupportedFileType(extension: string): boolean {
    return Object.values(SupportedFileTypes).includes(extension as SupportedFileTypes);
  }

  /**
   * Get file category based on extension
   * @param extension - File extension
   * @returns File category
   */
  private getFileCategory(extension: string): FileCategory {
    const ext = extension.toLowerCase();
    
    // Documents
    if (['pdf', 'docx', 'doc', 'pptx', 'txt'].includes(ext)) {
      return FileCategory.DOCUMENT;
    }
    
    // Images
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
      return FileCategory.IMAGE;
    }
    
    // Videos
    if (['mp4', 'avi', 'mov'].includes(ext)) {
      return FileCategory.VIDEO;
    }
    
    // Audio
    if (['mp3', 'wav', 'm4a'].includes(ext)) {
      return FileCategory.AUDIO;
    }
    
    // Data
    if (['csv', 'json', 'xml'].includes(ext)) {
      return FileCategory.DATA;
    }
    
    return FileCategory.UNKNOWN;
  }

  /**
   * Construct file URL from path
   * @param filePath - Storage file path
   * @returns Full URL to access the file
   */
  private constructFileUrl(filePath: string): string {
    // This would typically be configured based on your storage provider
    // For now, return the path as-is since the actual URL should come from the backend
    return filePath;
  }

  /**
   * Format file size for display
   * @param bytes - File size in bytes
   * @returns Formatted file size string
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Generate unique filename to avoid collisions
   * @param originalName - Original filename
   * @returns Unique filename with timestamp
   */
  generateUniqueFilename(originalName: string): string {
    const timestamp = Date.now();
    const extension = this.getFileExtension(originalName);
    const nameWithoutExt = originalName.substring(0, originalName.lastIndexOf('.'));
    
    return `${nameWithoutExt}_${timestamp}.${extension}`;
  }

  /**
   * Convert MIME type to file extension
   * @param mimeType - MIME type from storage API
   * @returns File extension
   */
  mimeTypeToExtension(mimeType: string): string {
    const mimeToExt: Record<string, string> = {
      // Documents
      'application/pdf': 'pdf',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'text/plain': 'txt',
      'text/csv': 'csv',
      'application/json': 'json',
      'application/xml': 'xml',
      
      // Images
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'image/svg+xml': 'svg',
      
      // Videos
      'video/mp4': 'mp4',
      'video/avi': 'avi',
      'video/quicktime': 'mov',
      'video/webm': 'webm',
      'video/x-msvideo': 'avi',
      
      // Audio
      'audio/mpeg': 'mp3',
      'audio/wav': 'wav',
      'audio/wave': 'wav',
      'audio/x-wav': 'wav',
      'audio/mp4': 'm4a',
      'audio/ogg': 'ogg',
      
      // Spreadsheets
      'application/vnd.ms-excel': 'xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
      
      // Presentations
      'application/vnd.ms-powerpoint': 'ppt',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
    };

    return mimeToExt[mimeType.toLowerCase()] || 'unknown';
  }
}

// Export singleton instance
export const storageService = new StorageService();